import React from 'react';

/* ⭐ THE ONE RUNTIME AUTHORITY FOR THE PROMOTED ENEMY-FAMILY STATUS VOCABULARY.
 *
 * Athero, Obstrux, Az-ma (internal key `asma`), the shared airway minis and Purpura
 * were authored and signed off on the admin tooltip workbench
 * (`admin-shell/tooltips/{athero,copd,asthma,itp}Review*`), against an engine that
 * did not yet run them. The backend promotion gives every one of those statuses a
 * real writer (`backend/services/mini_boss_engine/families/*.py`, applied by
 * `batch_combat_engine._compute_enemy_attack` and normalized by
 * `ENCOUNTER_STATUS_LIFECYCLES`), so the vocabulary belongs in the ordinary learner
 * runtime rather than behind an admin build flag.
 *
 * ⛔ THIS FILE IS METADATA, NEVER MECHANICS. It holds the glyph, the colour, the
 *    display name and the one-line def for each status — the things a RENDERER needs.
 *    It holds no magnitudes, no cadences, no formulas and no per-boss branching: every
 *    number on screen comes from the server's serialized `attack_spec` /
 *    `status_deltas`, resolved at render time by `decode.jsx`. A number typed here
 *    would be a second combat-mechanics authority and would drift the moment the
 *    roster is retuned (the `volume_warden` lesson in announceDecode.js).
 *
 * ⛔ ONE MODULE, THREE CONSUMERS, so promotion cannot fork:
 *      statusWordGlyphs.jsx  spreads PROMOTED_STATUS_TAG_ICONS / PROMOTED_STATUS_WORDS
 *      statusTags.jsx        spreads PROMOTED_TAG_META
 *      the wire binding      PROMOTED_STATE_KEY_TAGS (BatchBossSession / CombatSummary)
 *    An admin preview surface imports the SAME exports; it does not keep its own copy.
 *
 * ⛔ THE FAMILY WHOSE FINAL HIERARCHY WAS DEFERRED IS DELIBERATELY ABSENT FROM THIS
 *    FILE, and it is not named here even to explain the omission: the absence gate
 *    matches its spelling anywhere under `frontend/src`, so restating it would turn
 *    this paragraph into the offender it is warning about (§17d — POINT AT THE RIP,
 *    NEVER RESTATE IT). The gate holds the one copy of the name, because a matcher
 *    has to: see `data/__tests__/r49RosterCollapseFrontend.test.js` §6, which asserts
 *    the absence on BOTH sides — no client spelling AND no backend family module.
 *    The rule it enforces is the general one: a status registered here without a
 *    writer is the "consumer that outlives its writer" defect pointed the other way.
 *    It would render, and nothing would notice.
 */

// The shared 24x24 stroke wrapper every Player*Tag / status glyph uses, so a promoted
// mark sits on exactly the same visual key as the pre-existing HUD tags.
const svgIcon = (children) => (size, className = 'status-word__icon') => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

/* ── The approved marks, promoted verbatim from the signed-off review glyph modules.
      Paths are byte-identical to `admin-shell/tooltips/*ReviewGlyphs.jsx` at
      27466efff — this is a promotion, not a redesign, so a redrawn path here would
      silently ship something other than what was reviewed. ────────────────────── */

// Athero — three lipid droplets stacking (atheroReviewGlyphs.jsx).
const lipidIcon = svgIcon(
  <path d="M8 4C6.2 6.4 5 8.5 5 10.3a3 3 0 0 0 6 0C11 8.5 9.8 6.4 8 4Zm8 0c-1.8 2.4-3 4.5-3 6.3a3 3 0 0 0 6 0C19 8.5 17.8 6.4 16 4ZM12 12c-1.8 2.4-3 4.5-3 6.3a3 3 0 0 0 6 0c0-1.8-1.2-3.9-3-6.3Z" />,
);

// Athero — hardened deposits fused onto a vessel wall (atheroReviewGlyphs.jsx).
const plaqueIcon = svgIcon(
  <path d="M7.5 8.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Zm8.5 2a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM8 20a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm9.5-1.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM10 11.5h4M10.4 15l3.2-2.6" />,
);

// Obstrux / shared airway — retained secretions over a clearance line (copdReviewGlyphs.jsx).
const mucusIcon = svgIcon(<>
  <path d="M8 4C6.2 6.5 5 8.5 5 10.5a3 3 0 0 0 6 0C11 8.5 9.8 6.5 8 4Z" />
  <path d="M16 7c-1.8 2.5-3 4.5-3 6.5a3 3 0 0 0 6 0C19 11.5 17.8 9.5 16 7Z" />
  <path d="M5 19c3-1 5-1 7 0s4 1 7 0" />
</>);

// Obstrux / shared airway — lungs with the expiratory limb blocked (copdReviewGlyphs.jsx).
const airTrapIcon = svgIcon(<>
  <path d="M11 5v14" />
  <path d="M11 9C8 6 5.5 6.5 4.5 9.5L3 15c-.6 2.2.7 4 2.8 4H8c1.7 0 3-1.3 3-3" />
  <path d="M13 9c3-3 5.5-2.5 6.5.5L21 15c.6 2.2-.7 4-2.8 4H16c-1.7 0-3-1.3-3-3" />
  <path d="M15.5 4.5h5v5" />
  <path d="M20.5 4.5 16 9" />
  <path d="M16 12h4.5" />
</>);

// Az-ma — a dormant inflammatory focus, primed but not yet sprung (asthmaReviewGlyphs.jsx).
const inflammationTrapIcon = svgIcon(<>
  <circle cx="12" cy="12" r="7" />
  <circle cx="12" cy="12" r="2.5" />
  <path d="M12 2.5V5M12 19v2.5M2.5 12H5M19 12h2.5" />
  <path d="m5.3 5.3 1.8 1.8m9.8 9.8 1.8 1.8m0-13.4-1.8 1.8m-9.8 9.8-1.8 1.8" />
</>);

// Az-ma — the same lungs, now clamped from both sides (asthmaReviewGlyphs.jsx).
const bronchospasmIcon = svgIcon(<>
  <path d="M11 5v14" />
  <path d="M11 9C8 6 5.5 6.5 4.5 9.5L3 15c-.6 2.2.7 4 2.8 4H8c1.7 0 3-1.3 3-3" />
  <path d="M13 9c3-3 5.5-2.5 6.5.5L21 15c.6 2.2-.7 4-2.8 4H16c-1.7 0-3-1.3-3-3" />
  <path d="M3.5 7.5h5M15.5 7.5h5" />
  <path d="m6.5 5-3 2.5 3 2.5m11-5 3 2.5-3 2.5" />
</>);

// Purpura — scattered platelets pulling apart (itpReviewGlyphs.jsx).
const plateletLossIcon = svgIcon(<>
  <ellipse cx="8" cy="8" rx="3.2" ry="2.4" transform="rotate(-20 8 8)" />
  <ellipse cx="16.3" cy="7.4" rx="3.2" ry="2.4" transform="rotate(18 16.3 7.4)" />
  <ellipse cx="12" cy="15.7" rx="3.5" ry="2.5" transform="rotate(-8 12 15.7)" />
  <path d="m6.7 7.7 2.6.6m5.7-1.7 2.6 1.2m-7 7.4 2.8.8" />
</>);

// Purpura — a single welling drop (itpReviewGlyphs.jsx).
const itpBleedIcon = svgIcon(<>
  <path d="M12 3.5C9.5 7.3 6.5 10.3 6.5 14a5.5 5.5 0 0 0 11 0c0-3.7-3-6.7-5.5-10.5Z" />
  <path d="M9.2 14.5c.3 1.6 1.3 2.5 3 2.8" />
</>);

// Sick Ul — a crescent-shaped erythrocyte (reviewed 8954 pack).
const sicklingIcon = svgIcon(
  <path d="M19 4c-5.8 1.2-10.2 6.2-10.2 12.1 0 1.4.2 2.7.7 3.9C5.6 18.6 3 15 3 10.9 3 6 7 2 11.9 2c2.7 0 5.2.7 7.1 2Z" />,
);

// Sick Ul — vessel walls pinching around a sickled cell (reviewed 8954 pack).
const vasoOcclusionIcon = svgIcon(<>
  <path d="M3 6.5h5.5c2.2 0 3.2 1.5 3.5 3.5.3-2 1.3-3.5 3.5-3.5H21" />
  <path d="M3 17.5h5.5c2.2 0 3.2-1.5 3.5-3.5.3 2 1.3 3.5 3.5 3.5H21" />
  <path d="M14.8 9.2c-2 .4-3.4 2-3.4 3.9 0 .5.1.9.2 1.3-1.5-.6-2.4-1.8-2.4-3.2 0-1.7 1.4-3.1 3.1-3.1.9 0 1.8.4 2.5 1.1Z" />
</>);

/* iconKey -> glyph factory. Spread into STATUS_TAG_ICONS (statusWordGlyphs.jsx) so a
   promoted pill, an inline prose mention and a result chip all draw the same SVG. */
export const PROMOTED_STATUS_TAG_ICONS = {
  lipid: lipidIcon,
  plaque: plaqueIcon,
  mucus: mucusIcon,
  airTrap: airTrapIcon,
  inflammationTrap: inflammationTrapIcon,
  bronchospasm: bronchospasmIcon,
  plateletLoss: plateletLossIcon,
  itpBleed: itpBleedIcon,
  sickling: sicklingIcon,
  vasoOcclusion: vasoOcclusionIcon,
};

/* Prose word -> inline glyph + colour, spread into STATUS_WORDS (statusWordGlyphs.jsx).
   Colours are the review's, so an inline mention reads identically to the HUD tag.

   ⛔ `bleed` CARRIES `glyphOnly`, and the reason is the `clock` precedent one file over:
      STATUS_WORDS matches WORDS IN PROSE, and "bleed" is an ordinary clinical verb that
      already appears in un-decoded enemy copy (ENEMY_STATUS_META.inr, "bleeds when high").
      A plain entry would repaint those sentences as a Purpura mechanic. The mark stays
      addressable by `renderStatusGlyph('bleed')` and by the `itp-bleed` pill; only the
      free-prose auto-upgrade is withheld. */
export const PROMOTED_STATUS_WORDS = {
  lipid: { color: 'rgb(251, 191, 36)', glyph: lipidIcon },
  plaque: { color: 'rgb(244, 63, 94)', glyph: plaqueIcon },
  plaques: { color: 'rgb(244, 63, 94)', glyph: plaqueIcon },
  mucus: { color: 'rgb(163, 230, 53)', glyph: mucusIcon },
  'air trap': { color: 'rgb(147, 51, 234)', glyph: airTrapIcon },
  'inflammation trap': { color: 'rgb(244, 114, 182)', glyph: inflammationTrapIcon },
  bronchospasm: { color: 'rgb(34, 211, 238)', glyph: bronchospasmIcon },
  'platelet loss': { color: 'rgb(244, 114, 182)', glyph: plateletLossIcon },
  bleed: { color: 'rgb(159, 18, 57)', glyph: itpBleedIcon, title: 'bleed', glyphOnly: true },
  sickling: { color: 'rgb(248, 113, 113)', glyph: sicklingIcon },
  'vaso-occlusion': { color: 'rgb(190, 24, 93)', glyph: vasoOcclusionIcon },
};

/* Tag word -> TAG_META entry, spread into TAG_META (statusTags.jsx). Names, pill
   labels, colours, `line` prose and `def` ledgers are the approved review copy.

   `def` wins over `line` everywhere (statusTooltipLines), so the def is the compact
   rule a player reads under an "Inflicts [chip]" row. None of these defs states a
   magnitude the server owns: the per-fight numbers arrive as `stack_delta` and are
   rendered by the `fromStackDelta` prefix grammar instead. */
/* ⛔ THE `line` ON THESE ENTRIES IS THE CANONICAL SHARED EXPLAINER, AND IT IS RENDERED,
 *    NOT MERELY STORED. Four of them (mucus, inflammation-trap, air-trap, platelet-loss)
 *    were re-authored 2026-08-23 to state what the engine actually does — naming the
 *    families that consume each status and the exact Dispense floor — because the earlier
 *    copy described the mechanic vaguely ("feeds the disease boss", "the active disease
 *    boss resolves it") and one of them omitted the minimum-1 clamp entirely.
 *
 * 🔑 A `line` THAT NOTHING RENDERS IS METADATA, NOT AN EXPLAINER. `statusTooltipLines`
 *    prefers `def` over `line`, so for a status carrying a def the prose is unreachable
 *    on the HUD chip. `hudCanonicalLine: true` is the OPT-IN that makes the chip render
 *    that line ABOVE the existing def row.
 *
 * ⛔ THE FLAG IS OPT-IN, AND THAT IS THE WHOLE POINT OF IT BEING A FLAG. The first cut of
 *    this work rendered `line` + `def` for EVERY promoted status, which quietly widened
 *    the tooltips of six statuses nobody had asked about (lipid, plaque, bronchospasm,
 *    itp-bleed, sickling, vaso-occlusion) — a behaviour change smuggled in as a fix.
 *    Only the four statuses whose canonical prose was re-authored carry the flag; every
 *    other entry keeps its prior `statusTooltipLines` behaviour byte for byte.
 *
 * 🔑 IT IS A CAPABILITY, NOT A LIST. A future status opts in by adding one field to its
 *    own row here — never by being named in the component, which stays free of
 *    per-status branching. `def` and `aside` keep their own jobs: the compact ledger form
 *    and the flavour line.
 */
export const PROMOTED_TAG_META = {
  lipid: {
    statusClass: 'lipid', iconKey: 'lipid', name: 'Lipid Stacks',
    color: 'rgb(251, 191, 36)',
    line: 'Lipid-scaling enemies hit harder for every stack on you.',
    def: [{ t: 'Tastes good?', italic: true }],
    aside: [{ t: 'Tastes good?', italic: true }],
  },
  plaque: {
    statusClass: 'plaque', iconKey: 'plaque', name: 'Atherosclerotic Plaque',
    color: 'rgb(244, 63, 94)',
    line: 'Permanent plaque. The scarred HP cannot be healed, so your healing ceiling drops.',
    def: [{ g: 'hp', v: '−5 max', chip: true }, { t: 'permanent' }],
    aside: [{ t: 'Some things stick.', italic: true }],
  },
  plaques: {
    statusClass: 'plaque', iconKey: 'plaque', name: 'Atherosclerotic Plaque',
    color: 'rgb(244, 63, 94)',
    line: 'Permanent plaque. The scarred HP cannot be healed, so your healing ceiling drops.',
    def: [{ g: 'hp', v: '−5 max', chip: true }, { t: 'permanent' }],
    aside: [{ t: 'Some things stick.', italic: true }],
  },
  mucus: {
    statusClass: 'mucus', iconKey: 'mucus', name: 'Mucus Load',
    hudCanonicalLine: true,
    color: 'rgb(163, 230, 53)',
    line: 'Retained airway mucus. Some airway enemies use it to raise activation risk or convert it into Air Trap.',
    def: [{ t: "Can't clear it.", italic: true }],
    aside: [{ t: "Can't clear it.", italic: true }],
  },
  'air-trap': {
    statusClass: 'air-trap', iconKey: 'airTrap', name: 'Air Trap', pillLabel: 'Air Trap',
    hudCanonicalLine: true,
    color: 'rgb(147, 51, 234)',
    // ⛔ A DECLARED EXCEPTION, NOT A MAGIC CONSTANT. The result chip lays its text over a
    //    12%-alpha fill of this same colour on a near-black surface, and this violet is
    //    dark enough there to be hard to read. `chipTextColor` names the lifted tone ONCE,
    //    in the colour authority, so the CSS never has to know a second value. The
    //    pre-existing `--end-organ` chip already does exactly this by hand in CSS; this is
    //    that decision, moved to where the colours live.
    chipTextColor: 'rgb(167, 139, 250)',
    line: 'Each stack lowers Dispense damage by 1 for this fight, to a minimum of 1.',
    def: [{ t: 'Each Dispense −1 per stack for this fight; no decay' }],
    aside: [{ t: "Can't get it out.", italic: true }],
    prose: false,
  },
  'inflammation-trap': {
    statusClass: 'inflammation-trap', iconKey: 'inflammationTrap',
    hudCanonicalLine: true,
    name: 'Inflammation Trap', pillLabel: 'Inflammation',
    color: 'rgb(244, 114, 182)',
    line: 'Dormant airway inflammation. Az-ma can activate accumulated stacks into Bronchospasm; eosinophilic Obstrux can convert it into Air Trap.',
    def: [{ t: 'Disease boss determines when and how dormant stacks activate' }],
    aside: [{ t: 'Quiet. For now.', italic: true }],
    prose: false,
  },
  bronchospasm: {
    statusClass: 'bronchospasm', iconKey: 'bronchospasm', name: 'Bronchospasm',
    pillLabel: 'Trap', color: 'rgb(34, 211, 238)',
    line: 'For this fight, each active stack reduces every Dispense by 1.',
    def: [{ t: 'Each Dispense −1 per stack for this fight; no decay' }],
    aside: [{ t: 'Room is getting tight.', italic: true }],
    prose: false,
  },
  'platelet-loss': {
    statusClass: 'platelet-loss', iconKey: 'plateletLoss', name: 'Platelet Loss',
    hudCanonicalLine: true,
    pillLabel: 'Platelet Loss', color: 'rgb(244, 114, 182)',
    line: 'A persistent encounter stack. Purpura-family attacks scale from it, and Megakara doubles new applications while alive.',
    def: [{ t: 'Each stack adds 2 HP to Bleed each round' }],
    aside: [{ t: 'Running thin.', italic: true }],
    prose: false,
  },
  'itp-bleed': {
    statusClass: 'itp-bleed', iconKey: 'itpBleed', name: 'Bleed', pillLabel: 'Bleed',
    color: 'rgb(159, 18, 57)',
    // Same reason as `air-trap` above: this crimson is the exact tone the pre-existing
    // `--end-organ` chip lifts, and to the exact same value.
    chipTextColor: 'rgb(251, 113, 133)',
    line: 'Ongoing bleeding worsens as immune platelet loss accumulates.',
    def: [{ t: '2 HP per Platelet Loss stack each round' }],
    aside: [{ t: 'Hard to stop.', italic: true }],
    prose: false,
  },
  sickling: {
    statusClass: 'sickling', iconKey: 'sickling', name: 'Sickling',
    pillLabel: 'Sickling', color: 'rgb(248, 113, 113)',
    line: 'Encounter-local HbS sickling load that intensifies vaso-occlusive ischemia.',
    def: [{ t: 'Each stack adds 2 HP to Vaso-occlusion each round' }],
    aside: [{ t: 'Bent out of shape.', italic: true }],
    prose: false,
  },
  'vaso-occlusion': {
    statusClass: 'vaso-occlusion', iconKey: 'vasoOcclusion', name: 'Vaso-occlusion',
    pillLabel: 'Vaso-occlusion', color: 'rgb(190, 24, 93)',
    chipTextColor: 'rgb(251, 113, 133)',
    line: 'Microvascular obstruction converts the encounter Sickling load into ischemic injury.',
    def: [{ t: '2 HP per Sickling stack each round' }],
    aside: [{ t: 'No way through.', italic: true }],
    prose: false,
  },
};

/* ⭐ THE WIRE BINDING — server key -> the tag that renders it, plus its LIFECYCLE.
 *
 * ⛔ A PROMOTED KEY IS NOT AUTOMATICALLY A PIECE OF PLAYER STATE, and conflating the
 *    two is a real defect rather than a tidiness point. This map originally exposed one
 *    flat `PROMOTED_STATE_KEYS` list that every consumer iterated, so
 *    `normalizeCombatUserState` wrote `itp_bleed: 0` into `user_state` on every
 *    normalize — inventing a slot for an event the engine never stores, and
 *    contradicting this file's own note that it is never written there. Nothing failed:
 *    a spurious 0 reads exactly like a status you are not carrying.
 *
 * 🔑 SO THE LIFECYCLE IS DECLARED, NOT INFERRED, and the two derived sets below are
 *    named for what they are FOR. A consumer now has to say whether it means "state the
 *    engine keeps" or "effect that can appear on a result chip"; it can no longer get the
 *    wrong one by reaching for the only list there was.
 *
 * `lifecycle` mirrors the engine's own vocabulary so the two can be compared directly:
 *   'encounter'   — `ENCOUNTER_STATUS_LIFECYCLES[key] === 'encounter'`; cleared at
 *                   encounter completion.
 *   'run_sequela' — `ENCOUNTER_STATUS_LIFECYCLES[key] === 'run_sequela'`; survives the
 *                   fight through the permanent-health path.
 *   'event'       — NOT in `ENCOUNTER_STATUS_LIFECYCLES` at all, and that absence is the
 *                   contract. The engine names it in
 *                   `family_round_effect.event_status_effect` and emits it as an attack
 *                   row identity; it is never a `user_state` pool.
 *
 * `hud` says whether the key earns its own player-bar chip.
 *
 * ⛔ `plaques` IS `hud: false` ON PURPOSE, AND IT IS NOT AN OVERSIGHT. Its authored
 *    specs carry `sequelae_source: 'plaque'`, so the same fact already reaches the
 *    player bar as a crimson SEQUELAE chip (PlayerSequelaeTags), which is the app's
 *    canonical rendering of permanently scarred HP. A second stack chip beside it
 *    would be two chips for one fact. The tag itself stays registered because the
 *    decode rows name it ("Converts [lipid] into [plaque]").
 * ⛔ `itp_bleed` KEEPS ITS TAG AND ITS RESULT CHIP AND GETS NO STATE SLOT. Purpura's
 *    decode names it, and the round-result strip labels the family bleed row through it,
 *    so it must stay registered here. What it must never do is reach the two consumers
 *    that write `user_state`.
 */
export const PROMOTED_STATE_KEY_TAGS = {
  lipid_stacks: { tag: 'lipid', lifecycle: 'encounter', hud: true },
  plaques: { tag: 'plaque', lifecycle: 'run_sequela', hud: false },
  air_trap: { tag: 'air-trap', lifecycle: 'encounter', hud: true },
  mucus: { tag: 'mucus', lifecycle: 'encounter', hud: true },
  inflammation_trap: { tag: 'inflammation-trap', lifecycle: 'encounter', hud: true },
  bronchospasm: { tag: 'bronchospasm', lifecycle: 'encounter', hud: true },
  platelet_loss: { tag: 'platelet-loss', lifecycle: 'encounter', hud: true },
  itp_bleed: { tag: 'itp-bleed', lifecycle: 'event', hud: false },
  sickling: { tag: 'sickling', lifecycle: 'encounter', hud: true },
  sickle_vaso_occlusion: { tag: 'vaso-occlusion', lifecycle: 'event', hud: false },
};

/** Every promoted key that can appear as a round-result EFFECT kind — the result chip
 *  registry and the summary label/class helpers read this one. Includes `itp_bleed`:
 *  a chip is a rendering of something that happened, not a claim about stored state. */
export const PROMOTED_EFFECT_KEYS = Object.keys(PROMOTED_STATE_KEY_TAGS);

/** ⭐ Every promoted key the ENGINE actually keeps in `user_state`. This is the ONLY set
 *  a consumer that writes player state may iterate — `normalizeCombatUserState` and the
 *  effect-kind -> state-key map in BatchBossSession. Event-lifecycle keys are excluded
 *  by construction, so no future family can leak one into state by being added here. */
export const PROMOTED_ENGINE_STATE_KEYS = PROMOTED_EFFECT_KEYS
  .filter((key) => PROMOTED_STATE_KEY_TAGS[key].lifecycle !== 'event');

/** The subset of engine state that earns its own player-bar chip, in render order. */
export const PROMOTED_HUD_STATE_KEYS = PROMOTED_ENGINE_STATE_KEYS
  .filter((key) => PROMOTED_STATE_KEY_TAGS[key].hud);

/** The tag word one promoted key renders as, or null when the key is not promoted. */
export function promotedTagForStateKey(key) {
  return PROMOTED_STATE_KEY_TAGS[String(key || '')]?.tag || null;
}

/** True when the engine keeps this key in `user_state` (i.e. it is not event-only). */
export function isPromotedEngineStateKey(key) {
  const entry = PROMOTED_STATE_KEY_TAGS[String(key || '')];
  return Boolean(entry && entry.lifecycle !== 'event');
}


/* ═══ CHROME ═══════════════════════════════════════════════════════════════════
 *
 * ⭐ THE COLOUR IS DECLARED ONCE, ABOVE, AND EVERY SURFACE READS IT FROM THERE.
 *
 * ⛔ THIS FILE USED TO BE THE "one colour authority" IN NAME ONLY. Each promoted status
 *    also had hand-written CSS: three `bls-status-header--<slug>` rules for the HUD /
 *    inline pill and two `batch-result-pill--<slug>` rules for the result chip, each
 *    restating the same rgb. Eight statuses, ~80 lines, and every one of them a copy that
 *    could drift from the `color` a few lines up with nothing to notice — the CSS would
 *    keep rendering the stale tone perfectly.
 *
 * 🔑 SO THE CHROME IS VARIABLE-DRIVEN AND THE VARIABLE IS SET AT RENDER TIME. One generic
 *    CSS block per surface consumes `--status-tag-rgb`; the components below stamp that
 *    variable from `meta.color`. Adding a family now touches zero stylesheets, and a
 *    colour can only be changed in one place.
 *
 * The `r, g, b` TRIPLE (not a full colour) is what the variable carries, because the
 * generic rules need alpha variants — `rgba(var(--status-tag-rgb), 0.55)` for the border,
 * 0.95 for the icon stroke, 0.12/0.34 for the chip fill. That is the SAME idiom the combat
 * result pills already use for `--keystone-accent-rgb`, reused rather than reinvented.
 * ══════════════════════════════════════════════════════════════════════════════ */

/** `rgb(r, g, b)` -> `"r, g, b"`, the shape a CSS custom property needs so the generic
 *  rules can build their own alpha variants. Returns null for anything it cannot parse,
 *  so an unparseable colour degrades to un-tinted chrome rather than to `rgba(, 0.55)`. */
export function rgbTriple(color) {
  const m = /^\s*rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/.exec(String(color || ''));
  return m ? `${m[1]}, ${m[2]}, ${m[3]}` : null;
}

/** True when this tag's chrome is driven by its declared colour rather than by a
 *  hand-written per-slug CSS rule. Derived from membership, so there is no second marker
 *  to keep in step: a status is generic exactly because it is promoted. */
export function isPromotedTag(word) {
  return Object.prototype.hasOwnProperty.call(PROMOTED_TAG_META, String(word || ''));
}

/** The inline custom properties one promoted status' chrome needs.
 *
 *  `--status-tag-rgb`      the declared colour, for border / icon / value / fill.
 *  `--status-tag-text-rgb` the result chip's text tone; identical to the colour unless the
 *                          status declares a `chipTextColor` (see `air-trap` / `itp-bleed`).
 *
 *  Returns undefined when there is nothing to stamp, so a caller can spread it
 *  unconditionally and non-promoted statuses keep their existing per-slug rules untouched. */
export function promotedChromeVars(meta) {
  const triple = rgbTriple(meta?.color);
  if (!triple) return undefined;
  return {
    '--status-tag-rgb': triple,
    '--status-tag-text-rgb': rgbTriple(meta.chipTextColor) || triple,
  };
}
